import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  addDoc,
  Timestamp,
  runTransaction,
  limit,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  FragranceFinanceBalance, 
  FragranceFinanceFlow, 
  FragranceFinanceMovement,
  FragranceFinanceReport 
} from '@/types/fragranceFinance';

// Collection references
const financeBalanceCollection = collection(db, 'fragrance_finance_balances');
const financeFlowCollection = collection(db, 'fragrance_finance_flows');
const financeMovementCollection = collection(db, 'fragrance_finance_movements');

// Initialize partner balance
export async function initializePartnerFinanceBalance(partnerId: string, partnerName: string, partnerCode: string): Promise<string> {
  try {
    const balanceData: Omit<FragranceFinanceBalance, 'id'> = {
      partnerId,
      partnerName,
      partnerCode,
      currentFinance: 0,
      totalReceived: 0,
      totalSettled: 0,
      lastUpdated: new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(financeBalanceCollection, balanceData);
    return docRef.id;
  } catch (error) {
    console.error('Error initializing partner finance balance:', error);
    throw error;
  }
}

// Get partner balance
export async function getPartnerFinanceBalance(partnerId: string): Promise<FragranceFinanceBalance | null> {
  try {
    const q = query(financeBalanceCollection, where('partnerId', '==', partnerId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() } as FragranceFinanceBalance;
  } catch (error) {
    console.error('Error getting partner finance balance:', error);
    throw error;
  }
}

// Record a sale (partner collects money)
export async function recordPartnerSale(
  partnerId: string, 
  amount: number, 
  codeId: string,
  description: string
): Promise<void> {
  return runTransaction(db, async (transaction) => {
    // Get current balance
    const balanceQuery = query(financeBalanceCollection, where('partnerId', '==', partnerId));
    const balanceSnapshot = await getDocs(balanceQuery);
    
    if (balanceSnapshot.empty) {
      throw new Error('Partner balance not found');
    }

    const balanceDoc = balanceSnapshot.docs[0];
    const currentBalance = balanceDoc.data() as FragranceFinanceBalance;
    
    // Calculate new balance
    const newBalance = currentBalance.currentFinance + amount;
    const newTotalReceived = currentBalance.totalReceived + amount;

    // Create movement record
    const movement: Omit<FragranceFinanceMovement, 'id'> = {
      partnerId,
      partnerName: currentBalance.partnerName,
      partnerCode: currentBalance.partnerCode,
      type: 'credit',
      source: 'sale',
      amount: amount,
      description,
      referenceId: codeId,
      referenceType: 'order',
      balanceBefore: currentBalance.currentFinance,
      balanceAfter: newBalance,
      createdBy: 'system',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Update balance
    transaction.update(doc(financeBalanceCollection, balanceDoc.id), {
      currentFinance: newBalance,
      totalReceived: newTotalReceived,
      lastUpdated: new Date(),
      updatedAt: new Date()
    });

    // Add movement record
    const movementRef = doc(financeMovementCollection);
    transaction.set(movementRef, movement);
  });
}

// Record commission earned
export async function recordPartnerCommission(
  partnerId: string,
  commissionAmount: number,
  codeId: string,
  description: string
): Promise<void> {
  return runTransaction(db, async (transaction) => {
    // Get current balance
    const balanceQuery = query(financeBalanceCollection, where('partnerId', '==', partnerId));
    const balanceSnapshot = await getDocs(balanceQuery);
    
    if (balanceSnapshot.empty) {
      throw new Error('Partner balance not found');
    }

    const balanceDoc = balanceSnapshot.docs[0];
    const currentBalance = balanceDoc.data() as FragranceFinanceBalance;
    
    // Calculate new balance (commission reduces what partner owes)
    const newBalance = currentBalance.currentFinance - commissionAmount;

    // Create movement record
    const movement: Omit<FragranceFinanceMovement, 'id'> = {
      partnerId,
      partnerName: currentBalance.partnerName,
      partnerCode: currentBalance.partnerCode,
      type: 'debit',
      source: 'commission',
      amount: commissionAmount,
      description,
      referenceId: codeId,
      referenceType: 'redemption',
      balanceBefore: currentBalance.currentFinance,
      balanceAfter: newBalance,
      createdBy: 'system',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Update balance
    transaction.update(doc(financeBalanceCollection, balanceDoc.id), {
      currentFinance: newBalance,
      lastUpdated: new Date(),
      updatedAt: new Date()
    });

    // Add movement record
    const movementRef = doc(financeMovementCollection);
    transaction.set(movementRef, movement);
  });
}

// Create finance flow (transaction)
export async function createFinanceFlow(
  flowData: Omit<FragranceFinanceFlow, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    // Get current balance for snapshot
    const balance = await getPartnerFinanceBalance(flowData.partnerId);
    if (!balance) {
      throw new Error('Partner balance not found');
    }

    const flow: Omit<FragranceFinanceFlow, 'id'> = {
      ...flowData,
      balanceBeforeFlow: balance.currentFinance,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(financeFlowCollection, flow);
    return docRef.id;
  } catch (error) {
    console.error('Error creating finance flow:', error);
    throw error;
  }
}

// Confirm finance flow
export async function confirmFinanceFlow(
  flowId: string,
  confirmedBy: 'partner' | 'admin',
  notes?: string
): Promise<void> {
  return runTransaction(db, async (transaction) => {
    const flowRef = doc(financeFlowCollection, flowId);
    const flowDoc = await transaction.get(flowRef);
    
    if (!flowDoc.exists()) {
      throw new Error('Finance flow not found');
    }

    const flowData = flowDoc.data() as FragranceFinanceFlow;
    
    // Check if already confirmed
    if (flowData.status === 'confirmed') {
      throw new Error('Flow has already been confirmed');
    }

    // Update confirmation
    const updateData: any = {
      status: 'confirmed',
      confirmedBy: confirmedBy,
      confirmedAt: new Date(),
      updatedAt: new Date()
    };

    if (notes) {
      updateData.notes = notes;
    }

    // Update partner balance
    const balanceQuery = query(financeBalanceCollection, where('partnerId', '==', flowData.partnerId));
    const balanceSnapshot = await getDocs(balanceQuery);
    
    if (!balanceSnapshot.empty) {
      const balanceDoc = balanceSnapshot.docs[0];
      const currentBalance = balanceDoc.data() as FragranceFinanceBalance;
      
      // Calculate new balance based on flow type
      let newBalance: number;
      let movementAmount: number;
      let movementType: 'credit' | 'debit';
      
      if (flowData.type === 'partner_to_admin') {
        // Partner pays admin (reduces positive balance or increases negative balance)
        newBalance = currentBalance.currentFinance - flowData.financeAmount;
        movementAmount = flowData.financeAmount;
        movementType = 'debit';
      } else {
        // Admin pays partner (reduces negative balance or increases positive balance)
        newBalance = currentBalance.currentFinance + flowData.financeAmount;
        movementAmount = flowData.financeAmount;
        movementType = 'credit';
      }

      const newTotalSettled = currentBalance.totalSettled + flowData.financeAmount;
      
      // Update balance
      transaction.update(doc(financeBalanceCollection, balanceDoc.id), {
        currentFinance: newBalance,
        totalSettled: newTotalSettled,
        lastUpdated: new Date(),
        updatedAt: new Date()
      });

      // Create movement record
      const movement: Omit<FragranceFinanceMovement, 'id'> = {
        partnerId: flowData.partnerId,
        partnerName: flowData.partnerName,
        partnerCode: flowData.partnerCode,
        type: movementType,
        source: 'settlement',
        amount: movementAmount,
        description: `Settlement: ${flowData.description || 'Financial settlement'}`,
        referenceId: flowId,
        referenceType: 'flow',
        balanceBefore: currentBalance.currentFinance,
        balanceAfter: newBalance,
        createdBy: confirmedBy,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const movementRef = doc(financeMovementCollection);
      transaction.set(movementRef, movement);
    }

    transaction.update(flowRef, updateData);
  });
}

// Get pending finance flows
export async function getPendingFinanceFlows(
  partnerId?: string,
  userRole?: 'partner' | 'admin'
): Promise<FragranceFinanceFlow[]> {
  try {
    let q = query(
      financeFlowCollection,
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    if (partnerId) {
      q = query(q, where('partnerId', '==', partnerId));
    }

    const querySnapshot = await getDocs(q);
    const flows = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FragranceFinanceFlow[];

    return flows;
  } catch (error) {
    console.error('Error getting pending finance flows:', error);
    throw error;
  }
}

// Get all finance flows (pending and confirmed)
export async function getAllFinanceFlows(
  partnerId?: string
): Promise<FragranceFinanceFlow[]> {
  try {
    let q = query(
      financeFlowCollection,
      orderBy('createdAt', 'desc')
    );

    if (partnerId) {
      q = query(q, where('partnerId', '==', partnerId));
    }

    const querySnapshot = await getDocs(q);
    const flows = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FragranceFinanceFlow[];

    return flows;
  } catch (error) {
    console.error('Error getting all finance flows:', error);
    throw error;
  }
}

// Get partner movements (transaction history)
export async function getPartnerMovements(
  partnerId: string,
  startDate?: Date,
  endDate?: Date,
  lastDoc?: DocumentSnapshot,
  pageSize: number = 50
): Promise<{ movements: FragranceFinanceMovement[], lastDoc: DocumentSnapshot | null }> {
  try {
    let q = query(
      financeMovementCollection,
      where('partnerId', '==', partnerId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    if (startDate && endDate) {
      q = query(
        financeMovementCollection,
        where('partnerId', '==', partnerId),
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const movements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FragranceFinanceMovement[];

    const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return { movements, lastDoc: newLastDoc };
  } catch (error) {
    console.error('Error getting partner movements:', error);
    throw error;
  }
}

// Generate finance report
export async function generateFinanceReport(
  partnerId: string,
  startDate: Date,
  endDate: Date
): Promise<FragranceFinanceReport> {
  try {
    // Get partner details
    const balance = await getPartnerFinanceBalance(partnerId);
    if (!balance) {
      throw new Error('Partner balance not found');
    }

    // Get all movements in the period
    const movements: FragranceFinanceMovement[] = [];
    let lastDoc: DocumentSnapshot | null = null;
    let hasMore = true;

    while (hasMore) {
      const result = await getPartnerMovements(partnerId, startDate, endDate, lastDoc || undefined);
      movements.push(...result.movements);
      lastDoc = result.lastDoc;
      hasMore = result.movements.length === 50;
    }

    // Calculate summary data
    let openingBalance = 0;
    let totalCredits = 0;
    let totalDebits = 0;
    let salesCredits = 0;
    let commissionCredits = 0;
    let settlementDebits = 0;
    let adjustments = 0;
    let creditTransactions = 0;
    let debitTransactions = 0;

    // Find opening balance
    const firstMovement = movements[movements.length - 1];
    if (firstMovement) {
      openingBalance = firstMovement.balanceBefore;
    }

    // Process movements
    movements.forEach(movement => {
      if (movement.type === 'credit') {
        totalCredits += movement.amount;
        creditTransactions++;
        
        switch (movement.source) {
          case 'sale':
            salesCredits += movement.amount;
            break;
          case 'commission':
            commissionCredits += movement.amount;
            break;
          case 'adjustment':
            adjustments += movement.amount;
            break;
        }
      } else {
        totalDebits += movement.amount;
        debitTransactions++;
        
        switch (movement.source) {
          case 'settlement':
            settlementDebits += movement.amount;
            break;
          case 'adjustment':
            adjustments -= movement.amount;
            break;
        }
      }
    });

    // Get pending settlements
    const pendingFlows = await getPendingFinanceFlows(partnerId);
    const pendingSettlements = pendingFlows.reduce((sum, flow) => sum + flow.financeAmount, 0);

    const report: FragranceFinanceReport = {
      partnerId: balance.partnerId,
      partnerName: balance.partnerName,
      partnerCode: balance.partnerCode,
      reportPeriod: {
        startDate,
        endDate
      },
      openingBalance,
      closingBalance: balance.currentFinance,
      totalCredits,
      totalDebits,
      netMovement: totalCredits - totalDebits,
      salesCredits,
      commissionCredits,
      settlementDebits,
      adjustments,
      totalTransactions: movements.length,
      creditTransactions,
      debitTransactions,
      pendingSettlements,
      movements
    };

    return report;
  } catch (error) {
    console.error('Error generating finance report:', error);
    throw error;
  }
}

// Reset partner balance (for fixing incorrect data)
export async function resetPartnerBalance(partnerId: string): Promise<void> {
  try {
    // Get current balance document
    const q = query(financeBalanceCollection, where('partnerId', '==', partnerId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const balanceDoc = querySnapshot.docs[0];
      
      // Reset to zero
      await updateDoc(doc(financeBalanceCollection, balanceDoc.id), {
        currentFinance: 0,
        totalReceived: 0,
        totalSettled: 0,
        lastUpdated: new Date(),
        updatedAt: new Date()
      });
      
      console.log(`Reset balance for partner ${partnerId}`);
    }
  } catch (error) {
    console.error('Error resetting partner balance:', error);
    throw error;
  }
}

// Sync partner balance with current sales from partner stats
export async function syncPartnerBalance(partnerId: string): Promise<{
  processed: number;
  updated: number;
  errors: string[];
}> {
  console.log(`Starting balance sync for partner: ${partnerId}`);
  
  const errors: string[] = [];
  let processed = 0;
  let updated = 0;
  
  try {
    // Get or create partner balance
    let balance = await getPartnerFinanceBalance(partnerId);
    if (!balance) {
      // Partner balance doesn't exist, we need partner details to create it
      const { getPartner } = await import('./partnerService');
      const partner = await getPartner(partnerId);
      await initializePartnerFinanceBalance(partnerId, partner.name, partner.code);
      balance = await getPartnerFinanceBalance(partnerId);
      if (!balance) {
        throw new Error('Failed to initialize partner balance');
      }
    }
    
    // Get partner stats from fragrance_partner_stats collection
    const { getPartnerStats } = await import('./partnerService');
    const partnerStats = await getPartnerStats(partnerId);
    
    if (!partnerStats) {
      console.log('No partner stats found');
      return { processed: 0, updated: 0, errors: ['No partner stats found'] };
    }
    
    console.log(`Partner stats:`, {
      totalSales: partnerStats.totalSales,
      totalRevenue: partnerStats.partnerRevenue + partnerStats.storeRevenue,
      partnerRevenue: partnerStats.partnerRevenue,
      salesBySource: partnerStats.salesBySource
    });
    
    // Calculate total sales collected by partner
    let totalSalesCollected = 0;
    let partnerSalesCount = 0;
    
    // Only count revenue where the partner's code is the source (they collected the money)
    if (partnerStats.revenueBySource && balance.partnerCode) {
      // Check for revenue under partner code
      const partnerSourceRevenue = partnerStats.revenueBySource[balance.partnerCode] || 0;
      totalSalesCollected = partnerSourceRevenue;
      partnerSalesCount = partnerStats.salesBySource?.[balance.partnerCode] || 0;
      
      console.log(`Revenue by source for ${balance.partnerCode}: ${partnerSourceRevenue}`);
      console.log('All revenue sources:', partnerStats.revenueBySource);
      
      processed = partnerSalesCount;
    }
    
    // Use the commission from partner stats (already calculated for all sales)
    const totalCommissionEarned = partnerStats.partnerRevenue || 0;
    
    console.log(`Total sales collected by ${balance.partnerCode}: ${totalSalesCollected} (${partnerSalesCount} sales), Total commission earned: ${totalCommissionEarned}`);
    
    // Calculate base balance (what partner owes before settlements)
    const baseBalance = totalSalesCollected - totalCommissionEarned;
    
    // Get all settlement movements to calculate total settled
    let totalSettled = 0;
    const movements = await getAllPartnerMovements(partnerId);
    
    // Calculate total settled from settlement movements
    movements.forEach(movement => {
      if (movement.source === 'settlement') {
        if (movement.type === 'debit') {
          totalSettled += movement.amount;
        } else {
          totalSettled -= movement.amount;
        }
      }
    });
    
    console.log(`Base balance: ${baseBalance}, Total settled: ${totalSettled}`);
    
    // Calculate current balance (base balance minus what's been settled)
    const currentFinance = baseBalance - totalSettled;
    
    // Update the balance
    const balanceQuery = query(financeBalanceCollection, where('partnerId', '==', partnerId));
    const balanceSnapshot = await getDocs(balanceQuery);
    
    if (!balanceSnapshot.empty) {
      const balanceDoc = balanceSnapshot.docs[0];
      
      await updateDoc(doc(financeBalanceCollection, balanceDoc.id), {
        currentFinance: currentFinance,
        totalReceived: totalSalesCollected,
        totalSettled: totalSettled,
        lastUpdated: new Date(),
        updatedAt: new Date()
      });
      
      updated = 1;
      
      console.log(`Balance updated: Sales collected = ${totalSalesCollected}, Commission = ${totalCommissionEarned}, Settled = ${totalSettled}, Current Balance = ${currentFinance}`);
    }
    
    return {
      processed,
      updated,
      errors
    };
  } catch (error) {
    console.error('Error during balance sync:', error);
    errors.push(error instanceof Error ? error.message : 'Unknown error');
    return {
      processed,
      updated,
      errors
    };
  }
}

// Helper function to get all movements for a partner
async function getAllPartnerMovements(partnerId: string): Promise<FragranceFinanceMovement[]> {
  const movements: FragranceFinanceMovement[] = [];
  let lastDoc: DocumentSnapshot | null = null;
  let hasMore = true;
  
  while (hasMore) {
    const result = await getPartnerMovements(partnerId, undefined, undefined, lastDoc || undefined);
    movements.push(...result.movements);
    lastDoc = result.lastDoc;
    hasMore = result.movements.length === 50;
  }
  
  return movements;
}

// Legacy function names for backward compatibility
export const initializePartnerEssenceBalance = initializePartnerFinanceBalance;
export const getPartnerEssenceBalance = getPartnerFinanceBalance;
export const createEssenceFlow = createFinanceFlow;
export const confirmEssenceFlow = confirmFinanceFlow;
export const getPendingEssenceFlows = getPendingFinanceFlows;
export const getAllEssenceFlows = getAllFinanceFlows;
export const generateEssenceReport = generateFinanceReport; 